import { Suspense, useMemo } from "react";

import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import { useRef, useState } from "react";
import {
  IonButton,
  IonLabel,
  IonModal,
  IonToast,
  IonIcon,
} from "@ionic/react";
import { checkmarkCircle } from "ionicons/icons";

import s from "./WLSubscriptionResponseDialog.module.scss";
import { useWLSubscriptionResponseDialog } from "./store";
import { WLButton } from "../../../Common/components/WLButton/WLButton";
import {
  powerUpIcons,
  subscriptionCommonStyles,
  SubscriptionType,
} from "../../../Common/constants/subscriptionConst";
import { getErrorMsg } from "../../../Common/utils/common";
import { bugNotif } from "../../api/bugsnag";
import { useFormsStore } from "../../../Common/store/common";

import coinsTwenty from "../../../Common/assets/logo/coinsTwenty.png";
import confettiWhite from "../../assets/animations/confetti+white_logo.json";
import filter from "lodash/filter";
import WLLottie from "../../../AppUser/components/WLLottie/WLLottie";

export const WLSubscriptionResponseDialog = () => {
  const modal = useRef<HTMLIonModalElement>(null);
  const [
    isOpen,
    title,
    description,
    icon,
    onConfirmText,
    onConfirm,
    onCancelText,
    onCancel,
    hideDialog,
    reset,
    subscription,
    hideSuccessAnimation,
  ] = useWLSubscriptionResponseDialog((state) => [
    state.isOpen,
    state.title,
    state.description,
    state.icon,
    state.onConfirmText,
    state.onConfirm,
    state.onCancelText,
    state.onCancel,
    state.hideDialog,
    state.reset,
    state.subscription,
    state.hideSuccessAnimation,
  ]);

  const [error, setError] = useState("");
  const setFormLoading = useFormsStore().setFormLoading;

  const dismiss = () => {
    setError("");
    modal.current?.dismiss();
    hideDialog();
    reset();
  };

  const subscriptionIcon = useMemo(() => {
    if (subscription === undefined || icon === "none") {
      return <></>;
    }

    return (
      <div className={s.subscriptionIconContainer}>
        <img
          src={
            !subscription?.isOneOff
              ? subscriptionCommonStyles[
                  (icon as SubscriptionType) ?? "Starter"
                ].cardIcon
              : (powerUpIcons[subscription.entriesPerMonth] ?? coinsTwenty)
          }
          className={s.subscriptionIcon}
          alt=""
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }, [subscription, icon]);

  const descriptionContent = useMemo(() => {
    const membershipTypes = [
      "PRO",
      "MVP",
      "LEGEND",
      "VIP",
      "STARTER",
      "GOLD TIER",
      "BRONZE TIER",
      "SILVER TIER",
      "PLATINUM TIER",
      "DIAMOND TIER",
    ];

    const membershipType = membershipTypes.find((type) =>
      description.includes(type)
    );

    if (membershipType && subscription?.benefits) {
      return (
        <>
          <IonLabel
            className={cx(s.description, "win-body normal gilroy-regular")}
          >
            {!subscription.isOneOff ? (
              <>
                You're now an{" "}
                <span className="win-body bold gilroy">
                  {membershipType} Member
                </span>{" "}
                🏆
              </>
            ) : (
              <>
                You've just secured a&nbsp;
                <wbr />
                <span className="win-body bold gilroy">
                  {membershipType}
                </span>{" "}
                power up 🏆
              </>
            )}
          </IonLabel>

          <div className={s.benefitsContainer}>
            {filter(
              subscription.benefits,
              (benefit) =>
                !subscription.isOneOff || benefit.indexOf("merch") === -1
            ).map((benefit, index) => (
              <div className={s.benefitItem} key={`${benefit}-${index}`}>
                <IonIcon icon={checkmarkCircle} color="success" />
                <span className="win-sub normal gilroy">{benefit}</span>
              </div>
            ))}
          </div>
        </>
      );
    }

    return (
      <IonLabel className={cx(s.description, "win-body normal gilroy-regular")}>
        {description}
      </IonLabel>
    );
  }, [description, subscription]);

  const confirmAction = async () => {
    try {
      setFormLoading(true);
      await onConfirm();
      setFormLoading(false);
    } catch (error) {
      setFormLoading(false);
      const { errorMsg, rawErrorMsg } = getErrorMsg(error);
      setError(errorMsg);
      bugNotif("confirmAction", rawErrorMsg);
    }
  };
  return (
    <IonModal
      id="subscription-response-dialog"
      className={s.responseDialog}
      backdropDismiss={true}
      ref={modal}
      isOpen={isOpen}
      onDidDismiss={dismiss}
    >
      <div className={s.detailsContainer}>
        {!hideSuccessAnimation && (
          <Suspense>
            <WLLottie
              className={s.confettiAnimation}
              animationData={confettiWhite}
              justOnce={true}
            />
          </Suspense>
        )}
      </div>

      <div
        className={cx(s.container, {
          [s.noIcon]: icon === "none",
        })}
      >
        {subscriptionIcon}
        <div className={s.modalContent}>
          <IonLabel className="win-header-5 medium winnersans ion-text-uppercase">
            {title}
          </IonLabel>

          {descriptionContent}

          <WLButton
            onClick={confirmAction}
            className={cx(
              s.proceedBtn,
              "red-submit-button wl-body-6 bold ion-no-margin"
            )}
            isFormComponent
          >
            {onConfirmText}
          </WLButton>
          {onCancelText !== undefined && onCancel !== undefined && (
            <IonButton
              onClick={onCancel}
              fill="clear"
              className="wl-body-6 red"
            >
              {onCancelText}
            </IonButton>
          )}
        </div>
      </div>
      <IonToast
        isOpen={!isEmpty(error)}
        message={error}
        color="danger"
        duration={3000}
        onDidDismiss={() => {
          setError("");
        }}
      />
    </IonModal>
  );
};
